import { WebSocketEntityType } from '@this+that/models';
import log from 'loglevel';

import {
  WebSocketEventCallback,
  WebSocketEventMeta,
  WebSocketEventType,
} from './WebSocketContext';

/**
 * Subscription ID type
 */
type SubscriptionId = number;

/**
 * Internal subscription representation
 */
type Subscription = {
  id: SubscriptionId;
  entityType: WebSocketEntityType;
  eventTypes: Set<WebSocketEventType>;
  callback?: WebSocketEventCallback<any>;
};

/**
 * Event emitter interface for WebSocket events
 * Defines the public API of the EventEmitter class
 */
export interface IEventEmitter {
  /**
   * Subscribe to WebSocket events
   * @param entityType - The entity type to listen for
   * @param eventType - Single event type or array of event types
   * @param callback - Callback to invoke when event is emitted
   * @returns Unsubscribe function
   */
  subscribe<T>(
    entityType: WebSocketEntityType,
    eventType: WebSocketEventType | WebSocketEventType[],
    callback?: WebSocketEventCallback<T>,
  ): () => void;

  /**
   * Emit an event to all matching subscribers
   * @param entityType - The entity type of the event
   * @param eventType - The event type
   * @param item - Optional item data to pass to subscribers
   * @param meta - What the message says about the item beyond the item itself
   */
  emit<T>(
    entityType: WebSocketEntityType,
    eventType: WebSocketEventType,
    item?: T,
    meta?: WebSocketEventMeta,
  ): void;

  /**
   * Clear all subscriptions
   */
  clear(): void;
}

/**
 * Event emitter for WebSocket subscriptions
 *
 * Implements a pub/sub pattern for WebSocket events, allowing multiple
 * subscribers to listen for specific entity and event type combinations.
 *
 * Features:
 * - Type-safe subscriptions with generics
 * - Multiple event type subscriptions in one call
 * - Automatic cleanup via returned unsubscribe functions
 * - Error isolation (subscriber errors don't affect other subscribers)
 *
 * @example
 * ```typescript
 * const emitter = new EventEmitter();
 *
 * // Subscribe to INSERT events for UserCommunication
 * const unsubscribe = emitter.subscribe(
 *   WebSocketEntityType.UserCommunication,
 *   WebSocketEventType.INSERT,
 *   ({ item }) => console.log('New communication:', item)
 * );
 *
 * // Emit an event
 * emitter.emit(
 *   WebSocketEntityType.UserCommunication,
 *   WebSocketEventType.INSERT,
 *   newCommunication
 * );
 *
 * // Cleanup
 * unsubscribe();
 * ```
 */
export class EventEmitter implements IEventEmitter {
  private subscriptions = new Map<SubscriptionId, Subscription>();
  private nextId = 0;

  /**
   * Subscribe to WebSocket events
   * @param entityType - The entity type to listen for (e.g., UserCommunication, InboxEntry)
   * @param eventType - Single event type or array of event types to listen for
   * @param callback - Callback function to invoke when matching event is emitted
   * @returns Unsubscribe function to remove this subscription
   */
  subscribe<T>(
    entityType: WebSocketEntityType,
    eventType: WebSocketEventType | WebSocketEventType[],
    callback?: WebSocketEventCallback<T>,
  ): () => void {
    const id = this.nextId++;
    const eventTypes = Array.isArray(eventType)
      ? new Set(eventType)
      : new Set([eventType]);

    this.subscriptions.set(id, {
      id,
      entityType,
      eventTypes,
      callback,
    });

    return () => {
      this.subscriptions.delete(id);
    };
  }

  /**
   * Emit an event to all matching subscribers
   * @param entityType - The entity type of the event
   * @param eventType - The specific event type
   * @param item - Optional item data to pass to subscribers
   * @param meta - What the message says about the item beyond the item itself
   */
  emit<T>(
    entityType: WebSocketEntityType,
    eventType: WebSocketEventType,
    item?: T,
    meta?: WebSocketEventMeta,
  ): void {
    this.subscriptions.forEach((subscription) => {
      if (
        subscription.entityType === entityType &&
        subscription.eventTypes.has(eventType)
      ) {
        try {
          subscription.callback?.({ eventType, entityType, item, ...meta });
        } catch (error) {
          log.error(
            `[EventEmitter] Subscription ${subscription.id} callback error:`,
            error,
          );
        }
      }
    });
  }

  /**
   * Clear all subscriptions
   * Useful for cleanup on unmount or reset
   */
  clear(): void {
    this.subscriptions.clear();
  }

  /**
   * Get the current number of active subscriptions
   * Useful for debugging and testing
   */
  getSubscriptionCount(): number {
    return this.subscriptions.size;
  }
}
